import type { AuthMode } from "./authMode";
import { hasClerkCode } from "./clerkErrors";

type EmailFactor = {
  strategy?: string;
  emailAddressId?: string;
};

export type SignInStart = {
  create: (params: { identifier: string }) => Promise<{
    supportedFirstFactors?: EmailFactor[] | null;
    firstFactorVerification?: { status?: string | null } | null;
  }>;
  prepareFirstFactor: (params: {
    strategy: "email_code";
    emailAddressId: string;
  }) => Promise<unknown>;
  /** Present on the live Clerk resource after the first prepare. */
  supportedFirstFactors?: EmailFactor[] | null;
};

export type SignUpStart = {
  create: (params: { transfer?: boolean; emailAddress?: string }) => Promise<unknown>;
  prepareEmailAddressVerification: (params: {
    strategy: "email_code";
  }) => Promise<unknown>;
};

const prepareSignInEmailCode = async (
  signIn: SignInStart,
  result: { supportedFirstFactors?: EmailFactor[] | null },
) => {
  const emailFactor = result.supportedFirstFactors?.find(
    (factor) => factor.strategy === "email_code",
  );
  if (emailFactor?.emailAddressId) {
    await signIn.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: emailFactor.emailAddressId,
    });
    return;
  }
  throw new Error("no_email_factor");
};

const startSignUp = async (signUp: SignUpStart, email: string) => {
  try {
    await signUp.create({ transfer: true });
  } catch {
    await signUp.create({ emailAddress: email });
  }
  await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
};

/**
 * Identifier-first: always start as sign-in. Unknown emails transfer to
 * sign-up so the widget never asks the user to pick a mode.
 */
export async function startEmailSignInOrUp(args: {
  email: string;
  signIn: SignInStart;
  signUp: SignUpStart;
}): Promise<AuthMode> {
  const email = args.email.trim();
  try {
    const result = await args.signIn.create({ identifier: email });
    if (result.firstFactorVerification?.status === "transferable") {
      await startSignUp(args.signUp, email);
      return "signUp";
    }
    await prepareSignInEmailCode(args.signIn, result);
    return "signIn";
  } catch (error) {
    if (!hasClerkCode(error, "form_identifier_not_found")) {
      throw error;
    }
    await startSignUp(args.signUp, email);
    return "signUp";
  }
}

/**
 * Send another email OTP on the current Clerk attempt. Does not create a
 * new sign-in / sign-up; the previous code stays valid until Clerk expires it.
 */
export async function resendEmailOtp(args: {
  mode: AuthMode;
  signIn: SignInStart;
  signUp: SignUpStart;
}): Promise<void> {
  if (args.mode === "signIn") {
    await prepareSignInEmailCode(args.signIn, args.signIn);
    return;
  }
  await args.signUp.prepareEmailAddressVerification({ strategy: "email_code" });
}
